import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { WorkflowCaseStatus } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'

/**
 * ListDemandForecasts リクエストのフィルタ型。
 * - sections: タブに対応するワークフロー断面 (新しい SINGLE_DAY と旧 daily 値を併記)
 * - targetDate: 対象日 (google.type.Date 形式)
 * - areaCodes: エリアフィルタ (空 = 全エリア)
 * - status: ステータスフィルタ (undefined = 全ステータス)。WorkflowCaseStatus で絞り込む。
 */
export type DemandForecastListFilter = {
  sections: WorkflowSection[]
  targetDate: { year: number; month: number; day: number }
  areaCodes: AreaCode[]
  status?: WorkflowCaseStatus
}

/**
 * タブキーの型。design/demand/forecast-list.html (2026-07-15 ユーザー指示 / 7/16
 * design-desk 同期版) L94-99 の data-tab 値と一致させる: 単日/週間/月間/年間 の
 * 4 タブ (当日 (翌日)タブは廃止し単日タブへ統合)。押せるかどうかは
 * TabDefinition の kind で決まる (月間/年間は押せない、AC-DFL-026)。
 */
export type DemandForecastTabKey = 'single' | 'weekly' | 'monthly' | 'annual'

/**
 * 単日タブの定義 (= 唯一の一覧取得対象タブ、kind 'data')。
 * 新しい SINGLE_DAY と、当日/翌日/翌々日の legacy 値を併せて取得する。
 * 新規 ID は 1 文字 `D` 形式で、単日内の旧区別は表示しない
 * (断面列は issue #4927 で削除済み)。
 */
export type SingleTabDefinition = {
  key: 'single'
  kind: 'data'
  /** i18n key (= page:demandForecastList.tab.<key>) */
  i18nKey: string
  /** ListDemandForecasts リクエストの sections フィルタ */
  sections: WorkflowSection[]
}

/**
 * 開発中プレースホルダタブ (週間、kind 'placeholder')。
 * design (2026-07-15 ユーザー指示): タブ自体はクリック可能だが、一覧取得を
 * 行わずテーブル/フィルタ領域を隠してプレースホルダを表示する
 * (index.tsx が useListDemandForecasts を enabled=false でスキップする)。
 */
export type PlaceholderTabDefinition = {
  key: 'weekly'
  kind: 'placeholder'
  /** i18n key (= page:demandForecastList.tab.<key>) */
  i18nKey: string
}

/**
 * 押せないタブ (月間/年間、kind 'unavailable'、AC-DFL-026)。
 * 需要想定 月間・年間 (P8) を提供するまで、タブは出したまま非活性にし、
 * 押せない理由をツールチップで出す。選択 state (SelectableTabKey) には入らない。
 * P8 を提供したら kind を戻す。
 * 年間の key は proto (WORKFLOW_SECTION_ANNUAL) / 既存 i18n (section.annual)
 * と同じ `annual` を使う (= ユビキタス名。`yearly` に fork しない)。
 */
export type UnavailableTabDefinition = {
  key: 'monthly' | 'annual'
  kind: 'unavailable'
  /** i18n key (= page:demandForecastList.tab.<key>) */
  i18nKey: string
}

/**
 * タブ定義 (discriminated union)。`kind` で分岐することで、単日タブのみが
 * sections を持つ構造を型で表現する (= 週間/月間/年間 は sections を持たない
 * ため誤って参照できない)。
 *
 * 行クリック遷移先 (/demand/forecast-daily?workflowId=…) は断面を含まない
 * (計画断面は画面側で対象日から判定する、AC-DFD-000)。
 */
export type TabDefinition =
  | SingleTabDefinition
  | PlaceholderTabDefinition
  | UnavailableTabDefinition

/** 選択できるタブのキー (= 押せないタブを除く)。選択 state はこの型だけを持つ。 */
export type SelectableTabKey = Exclude<
  DemandForecastTabKey,
  UnavailableTabDefinition['key']
>

type SelectableTabDefinition = Exclude<TabDefinition, UnavailableTabDefinition>

export const TAB_DEFINITIONS: TabDefinition[] = [
  {
    key: 'single',
    kind: 'data',
    i18nKey: 'page:demandForecastList.tab.singleDay',
    // 新しい SINGLE_DAY と旧 daily 値を同じ単日タブで取得する。
    // 新規 ID は 1 文字 D 形式で、旧 D0/D1/D2 ID は読取互換として残る。
    sections: [
      WorkflowSection.SINGLE_DAY,
      WorkflowSection.TODAY,
      WorkflowSection.TOMORROW,
      WorkflowSection.DAY_AFTER_TOMORROW,
    ],
  },
  {
    key: 'weekly',
    kind: 'placeholder',
    i18nKey: 'page:demandForecastList.tab.weekly',
  },
  {
    key: 'monthly',
    kind: 'unavailable',
    i18nKey: 'page:demandForecastList.tab.monthly',
  },
  {
    key: 'annual',
    kind: 'unavailable',
    i18nKey: 'page:demandForecastList.tab.annual',
  },
]

const isSelectableTabDefinition = (
  tab: TabDefinition,
): tab is SelectableTabDefinition => tab.kind !== 'unavailable'

const SELECTABLE_TAB_DEFINITIONS: SelectableTabDefinition[] =
  TAB_DEFINITIONS.filter(isSelectableTabDefinition)

/**
 * 選択できるタブのキーから TabDefinition を取得する。
 * 見つからない場合は最初のタブ定義 (単日) を返す。
 */
export function getTabDefinition(
  key: SelectableTabKey,
): SingleTabDefinition | PlaceholderTabDefinition {
  return (
    SELECTABLE_TAB_DEFINITIONS.find((t) => t.key === key) ??
    SELECTABLE_TAB_DEFINITIONS[0]
  )
}

/**
 * タブ部品から来た value を選択できるタブのキーに絞る。
 * 押せないタブ (kind 'unavailable') と未知の値は undefined を返す
 * (= 選択 state に入れない)。
 */
export function toSelectableTabKey(
  value: string,
): SelectableTabKey | undefined {
  return SELECTABLE_TAB_DEFINITIONS.find((t) => t.key === value)?.key
}

/**
 * ForecastTable の全列キー (表示順)。
 * 「断面」列は spec AC-DFL-006 (2026-08-04 issue #4263 確定) により削除済み
 * (新規単日 ID は 1 文字 `D` 形式で、旧区別も表示しない、issue #4927)。
 * 残る列は全タブ共通で、「評価」列 (eval) も全タブ表示 (spec §1 AC-003)。
 */
export const ALL_FORECAST_TABLE_COLUMN_KEYS = [
  'workflow',
  'targetPeriod',
  'area',
  // BG は「BG コード」「BG 名称」 の 2 列 (2026-09-01 デザイナー指摘)。
  //
  // ⚠️ spec AC-DFL-006 [P0]「BG コードと BG コード名称を 1 列内に併記」 と衝突する。
  // 同 AC は 2026-08-28 の PdM 判断で 2 列 → 1 列へ統合したもので、 当時の理由は
  // 「同じ BG コードが 1 行に 2 回出る」 こと (旧 2 列目が「BG コード名称」 =
  // コードと名称の両方を含む列だった)。 本変更の 2 列目は **名称のみ**なので
  // その重複は起きない。 AC の再判断は issue #6952 で PdM に確認中。
  // **解決前にマージしないこと。**
  'bgCode',
  'bgName',
  'status',
  'createdAt',
  'completedAt',
  'completedBy',
  'peakDemandMw',
  'energyMwh',
  'eval',
] as const

export type ForecastTableColumnKey =
  (typeof ALL_FORECAST_TABLE_COLUMN_KEYS)[number]

/**
 * 今日を基準に対象日デフォルト値を計算する。
 * @param today - 今日の Date (= テストで注入できるよう引数化)
 * @param offsetDays - 今日からのオフセット日数
 */
export function computeDefaultTargetDate(
  today: Date,
  offsetDays: number,
): Date {
  const result = new Date(today)
  result.setDate(result.getDate() + offsetDays)
  return result
}

/**
 * Date を google.type.Date 形式の object に変換する。
 */
export function dateToProtoDate(d: Date): {
  year: number
  month: number
  day: number
} {
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
  }
}

/**
 * proto google.type.Date を YYYY/MM/DD 形式の表示文字列に変換する。
 */
export function formatProtoDate(
  d: { year: number; month: number; day: number } | undefined,
): string {
  if (d === undefined) return '—'
  return `${String(d.year)}/${String(d.month).padStart(2, '0')}/${String(d.day).padStart(2, '0')}`
}
